import { useState, useRef, useEffect, useCallback } from "react";
import type { DachCity } from "@/components/inquiry/LocationPicker";

interface CityAutocompleteProps {
  value: string;
  postalCode: string;
  onSelect: (city: string, postalCode: string) => void;
  placeholder?: string;
  className?: string;
}

export default function CityAutocomplete({ value, postalCode, onSelect, placeholder = "z.B. München", className }: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<DachCity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [confirmed, setConfirmed] = useState(!!value);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync external value changes
  useEffect(() => { setQuery(value); setConfirmed(!!value); }, [value]);

  const searchCities = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/location-search?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${supabaseKey}`, apikey: supabaseKey } }
      );
      if (res.ok) {
        const cities: DachCity[] = await res.json();
        setResults(cities);
        setOpen(cities.length > 0);
        setHighlightIndex(-1);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const handleInput = (val: string) => {
    setQuery(val);
    setConfirmed(false);
    onSelect("", "");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCities(val), 300);
  };

  const selectCity = (city: DachCity) => {
    setQuery(city.name);
    setConfirmed(true);
    setOpen(false);
    setResults([]);
    onSelect(city.name, city.postalCode);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIndex(p => Math.min(p + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIndex(p => Math.max(p - 1, 0)); }
    else if (e.key === "Enter" && highlightIndex >= 0) { e.preventDefault(); selectCity(results[highlightIndex]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-3.5 h-3.5 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 border border-border bg-card shadow-lg max-h-52 overflow-y-auto">
          {results.map((city, i) => (
            <li
              key={`${city.country}-${city.postalCode}-${city.name}`}
              role="option"
              aria-selected={highlightIndex === i}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors ${
                highlightIndex === i ? "bg-accent/10 text-foreground" : "hover:bg-muted/50"
              }`}
              onMouseEnter={() => setHighlightIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); selectCity(city); }}
            >
              <span className="shrink-0">{city.flag}</span>
              <span className="truncate">{city.name}</span>
              <span className="text-xs text-muted-foreground ml-auto">{city.postalCode}</span>
            </li>
          ))}
        </ul>
      )}

      {query.length >= 2 && !loading && results.length === 0 && !confirmed && (
        <p className="text-xs text-destructive mt-1">Kein Ort im DACH-Raum gefunden.</p>
      )}
    </div>
  );
}
